/**
 * Serviço BFF (Backend for Frontend) para Recommendation Engine
 * Implementa o padrão de desacoplamento: Frontend → Data-Provider → Frontend → Recommendation-Engine
 */

interface ProviderData {
  status: 'SUCCESS' | 'ERROR' | 'TIMEOUT';
  data?: any;
  error_message?: string;
}

interface AnalysisRequest {
  social_id: string;
  document_type: 'CPF' | 'CNPJ';
  business_area: 'PLD' | 'CREDIT' | 'COMPLIANCE';
  provider_data: Record<string, ProviderData>;
  additional_data?: Record<string, any>;
}

interface AnalysisResponse {
  analysis_id: string;
  social_id: string;
  recommendation: 'APROVADO' | 'REPROVADO' | 'PENDENTE';
  risk_score: number;
  analysis_date: string;
  rules_applied: string[];
  message: string;
}

class RecommendationBFFService {
  private readonly API_KEY: string;
  private readonly DATA_PROVIDER_BASE_URL: string;
  private readonly RECOMMENDATION_ENGINE_BASE_URL: string;

  constructor() {
    this.API_KEY = process.env.REACT_APP_ROUTER_API_KEY || process.env.REACT_APP_API_KEY || '';
    const routerUrl = (process.env.REACT_APP_ROUTER_API_URL || '').replace(/\/$/, ''); // Remove trailing slash
    this.DATA_PROVIDER_BASE_URL = `${routerUrl}/data-provider`;
    this.RECOMMENDATION_ENGINE_BASE_URL = `${routerUrl}/recommendation-engine`;
  }

  /**
   * Fluxo completo BFF: busca dados + análise de recomendação
   * A unificação por documento é feita apenas no frontend da tela de recomendações
   */
  async analyzeBatch(batchId: string, onProgress?: (processed: number, total: number) => void): Promise<any> {
    try {
      // Buscar dados do lote no data-provider (já inclui reports processados)
      const batchData = await this.fetchBatchData(batchId);

      // Preparar payload batch com todos os documentos
      if (batchData.reports && Array.isArray(batchData.reports)) {
        let documents: any[];
        
        // ✅ ESCAVADOR: Enviar 1 documento por processo (não agrupar)
        if (batchData.provider === 'ESCAVADOR') {
          documents = batchData.reports.map((report: any) => {
            const document = report.document || report.social_id;
            
            const excludeFields = ['document', 'name', 'status', 'statusFormatted', 'createdAt', 'updatedAt',
              'backend_recommendation', 'backend_reasons', 'backend_risk_score',
              'backend_analysis_id', 'backend_document_type', 'reportType', 'reportTypeId',
              'batchReportId', 'attempts', 'maxAttempts', 'events', 'analysis_status', 'pep_reason'];
            
            const reportData: any = {};
            Object.keys(report).forEach(key => {
              if (!excludeFields.includes(key)) {
                reportData[key] = report[key];
              }
            });
            
            return {
              social_id: document,
              document_type: document.replace(/\D/g, '').length === 11 ? 'CPF' : 'CNPJ',
              business_area: 'PLD',
              process_id: report.numero_processo || '',  // ✅ Identificador único do processo
              provider_data: {
                ESCAVADOR: {
                  status: 'SUCCESS',
                  data: reportData  // ✅ Dados do processo específico
                }
              }
            };
          });
          
          console.log(`✅ ESCAVADOR: ${documents.length} processos individuais para análise`);
          console.log(`📤 Exemplo de documento enviado:`, documents[0]);
          console.log(`📋 Process IDs enviados:`, documents.slice(0, 5).map((d: any) => `${d.social_id} → "${d.process_id}"`));
        } else {
          // ✅ INOVAMIND/QUOD: Manter lógica atual (1 report = 1 documento)
          documents = batchData.reports.map((report: any) => {
            const providerData: Record<string, ProviderData> = {};
            const excludeFields = ['document', 'name', 'status', 'statusFormatted', 'createdAt', 'updatedAt',
              'backend_recommendation', 'backend_reasons', 'backend_risk_score',
              'backend_analysis_id', 'backend_document_type', 'reportType', 'reportTypeId',
              'batchReportId', 'attempts', 'maxAttempts', 'events', 'analysis_status', 'pep_reason'];
            const reportData: any = {};
            Object.keys(report).forEach(key => {
              if (!excludeFields.includes(key)) {
                reportData[key] = report[key];
              }
            });
            const providerName = batchData.provider || 'UNKNOWN';
            providerData[providerName] = {
              status: 'SUCCESS',
              data: reportData
            };
            return {
              social_id: report.document || report.social_id,
              document_type: report.document_type || 'CNPJ',
              business_area: 'PLD',
              provider_data: providerData
            };
          });
        }

        // Enviar documentos com chunking para evitar timeout
        const batchResults = await this.sendBatchToRecommendationEngineWithChunking(
          documents,
          200,
          (processed, total) => {
            // Callback externo (UI)
            if (onProgress) {
              onProgress(processed, total);
            }
            // Log de progresso reduzido
            if (processed % 50 === 0 || processed === total) {
              console.info(`[RecommendationBFFService] Progresso: ${processed}/${total} (${Math.round(processed / total * 100)}%)`);
            }
          }
        );

        // Enriquecer reports com resultados
        if (batchData.provider === 'ESCAVADOR') {
          // ESCAVADOR: Mapear por process_id (1:1 com resultado)
          console.log(`📊 ESCAVADOR: ${batchData.reports.length} reports, ${batchResults.results.length} resultados`);
          console.log(`📊 Primeiro resultado:`, batchResults.results[0]);
          
          batchData.reports.forEach((report: any) => {
            // 🎯 Estratégia de matching com fallback
            let result = null;
            
            // 1ª tentativa: Match exato (social_id + process_id)
            if (report.numero_processo) {
              result = batchResults.results.find((r: any) => 
                r.social_id === report.document && 
                r.process_id === report.numero_processo
              );
              if (result) {
                console.log(`✅ Match exato: ${report.document} + processo ${report.numero_processo}`);
              }
            }
            
            // 2ª tentativa: Fallback por social_id apenas (quando process_id vazio ou não encontrado)
            if (!result) {
              result = batchResults.results.find((r: any) => 
                r.social_id === report.document
              );
              if (result) {
                console.log(`⚠️ Match por fallback (social_id): ${report.document} → ${result.recommendation}`);
              }
            }
            
            if (result) {
              report.backend_recommendation = result.recommendation;
              report.backend_reasons = result.rules_applied;
              report.backend_risk_score = result.risk_score;
              report.backend_analysis_id = result.analysis_id;
              report.backend_credit_flag = result.credit_flag;
              report.backend_pld_flag = result.pld_flag;
              console.log(`✅ ESCAVADOR: Processo ${report.numero_processo || 'SEM_PROCESSO'} → ${result.recommendation}`);
            } else {
              console.error(`❌ ESCAVADOR: Nenhum resultado encontrado para ${report.document}`);
              console.error(`   process_id enviado: "${report.numero_processo}"`);
              console.error(`   Resultados disponíveis:`, batchResults.results.filter((r: any) => r.social_id === report.document));
            }
          });
        } else {
          // INOVAMIND/QUOD: Manter lógica atual (1:1)
          batchData.reports.forEach((report: any, index: number) => {
            const result = batchResults.results[index];
            if (result) {
              report.backend_recommendation = result.recommendation;
              report.backend_reasons = result.rules_applied;
              report.backend_risk_score = result.risk_score;
              report.backend_analysis_id = result.analysis_id;
              report.backend_credit_flag = result.credit_flag;
            }
          });
        }
      }
      return batchData;

    } catch (error) {
      console.error('❌ Erro na análise batch do lote:', error);
      throw error;
    }
  }

  /**
   * Envia lote completo para recommendation-engine (1 única requisição)
   * Método público para uso em telas que precisam análise batch direta
   * Implementa retry automático com backoff linear
   */
  async sendBatchToRecommendationEngine(documents: any[], maxRetries: number = 3): Promise<any> {
    const url = `${this.RECOMMENDATION_ENGINE_BASE_URL}/recommendations/analyze-batch`;

    let lastError: Error | null = null;

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      console.log(`🔄 [Batch] Tentativa ${attempt}/${maxRetries} - ${documents.length} documentos`);

      // Timeout de 120 segundos para processar lote grande
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 120000);

      try {
        const response = await fetch(url, {
          method: 'POST',
          headers: {
            'x-api-key': this.API_KEY,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ documents }),
          signal: controller.signal
        });

        clearTimeout(timeoutId);

        if (!response.ok) {
          const errorText = await response.text();
          console.error(`❌ [Batch] Erro na tentativa ${attempt}:`, errorText);
          
          if (attempt < maxRetries) {
            const delay = 1000 * attempt; // 1s, 2s, 3s
            console.log(`⏳ [Batch] Aguardando ${delay}ms antes de retry...`);
            await new Promise(resolve => setTimeout(resolve, delay));
            continue;
          }
          
          throw new Error(`Batch analysis error: ${response.status} ${response.statusText} - ${errorText}`);
        }

        const result = await response.json();
        
        // Backend retorna status 207 se houver falhas parciais
        if (result.failures && result.failures.length > 0) {
          console.warn(`⚠️ [Batch] ${result.failures.length} documentos falharam:`, result.failures);
        }
        
        console.log(`✅ [Batch] Processado: ${result.success} sucessos, ${result.errors} erros`);
        return result;

      } catch (error) {
        clearTimeout(timeoutId);
        lastError = error as Error;
        
        if (error instanceof Error && error.name === 'AbortError') {
          console.error(`❌ [Batch] Timeout na tentativa ${attempt}`);
          if (attempt < maxRetries) {
            const delay = 2000 * attempt; // 2s, 4s, 6s para timeout
            console.log(`⏳ [Batch] Aguardando ${delay}ms antes de retry...`);
            await new Promise(resolve => setTimeout(resolve, delay));
            continue;
          }
          throw new Error('Timeout ao processar batch (120s). Tente com menos documentos ou aguarde.');
        }
        
        console.error(`❌ [Batch] Erro na tentativa ${attempt}:`, error);
        if (attempt < maxRetries) {
          const delay = 1000 * attempt;
          console.log(`⏳ [Batch] Aguardando ${delay}ms antes de retry...`);
          await new Promise(resolve => setTimeout(resolve, delay));
        }
      }
    }

    console.error('❌ [Batch] Todas as tentativas falharam');
    throw lastError || new Error('Failed to process batch after retries');
  }

  /**
   * Envia lote grande em chunks para evitar timeout do API Gateway (29s)
   * Processa N documentos por vez com progresso
   */
  async sendBatchToRecommendationEngineWithChunking(
    documents: any[],
    chunkSize: number = 200,
    onProgress?: (processed: number, total: number) => void,
    concurrency: number = 4
  ): Promise<any> {
    const allResults: any[] = [];
    let totalProcessed = 0;
    let totalErrors = 0;
    const totalChunks = Math.ceil(documents.length / chunkSize);

    // Log mínimo: apenas erro

    // Dividir documentos em chunks
    const chunks = [];
    for (let i = 0; i < documents.length; i += chunkSize) {
      chunks.push(documents.slice(i, i + chunkSize));
    }

    // Processamento paralelo limitado
    let processedCount = 0;
    const processChunk = async (chunk: any[], chunkNumber: number) => {
      try {
        const chunkResult = await this.sendBatchToRecommendationEngine(chunk);
        allResults.push(...chunkResult.results);
        totalProcessed += chunkResult.total_processed;
        totalErrors += chunkResult.total_errors;
        processedCount += chunk.length;
        if (onProgress) {
          onProgress(processedCount, documents.length);
        }
      } catch (error) {
        console.error(`[RecommendationBFFService] Erro no chunk ${chunkNumber + 1}/${totalChunks}:`, error);
        totalErrors += chunk.length;
        allResults.push(...chunk.map(doc => ({
          social_id: doc.social_id,
          document_type: doc.document_type,
          recommendation: 'ERRO',
          rules_applied: [],
          risk_score: 0,
          error: 'Chunk processing failed'
        })));
      }
    };

    // Fila de execução paralela limitada
    const queue: Promise<void>[] = [];
    let idx = 0;
    while (idx < chunks.length) {
      while (queue.length < concurrency && idx < chunks.length) {
        queue.push(processChunk(chunks[idx], idx));
        idx++;
      }
      await Promise.race(queue);
      // Remove a primeira promessa resolvida da fila
      queue.shift();
    }
    // Espera todos terminarem
    await Promise.all(queue);

    console.log(`✅ Processamento completo: ${totalProcessed}/${documents.length} processados, ${totalErrors} erros`);

    return {
      results: allResults,
      total_processed: totalProcessed,
      total_errors: totalErrors
    };
  }

  /**
   * Busca dados de um lote específico
   */
  async fetchBatchData(batchId: string): Promise<any> {
    const url = `${this.DATA_PROVIDER_BASE_URL}/batch-reports/${batchId}/page-view`;

    const response = await fetch(url, {
      headers: {
        'x-api-key': this.API_KEY,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error(`Batch data error: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();

    // Se retornou erro interno, lançar exceção
    if (data.error) {
      throw new Error(`API error: ${data.error}`);
    }

    console.log('📦 Dados do lote retornados:', data);
    return data;
  }

  /**
   * Lista todos os lotes disponíveis (usa data-provider diretamente)
   */
  async fetchBatchList(): Promise<any> {
    // Para listagem de lotes, usar data-provider diretamente é correto
    const url = `${this.DATA_PROVIDER_BASE_URL}/batch-reports/page-list`;

    const authToken = localStorage.getItem('authToken');
    const response = await fetch(url, {
      headers: {
        'x-api-key': this.API_KEY,
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authToken}`
      }
    });

    if (!response.ok) {
      throw new Error(`Batch list error: ${response.status} ${response.statusText}`);
    }

    return await response.json();
  }

  /**
   * Lista lotes com filtros (compatível com PageList)
   */
  async fetchBatchListWithFilters(params: URLSearchParams): Promise<any> {
    const url = `${this.DATA_PROVIDER_BASE_URL}/batch-reports/page-list?${params.toString()}`;

    const response = await fetch(url, {
      headers: {
        'x-api-key': this.API_KEY,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error(`Batch list error: ${response.status} ${response.statusText}`);
    }

    return await response.json();
  }

  /**
   * Busca último parecer salvo para um batch
   */
  /**
   * Salva parecer PLD consolidado
   */
  async savePLDReport(parecerData: any): Promise<any> {
    const url = `${this.RECOMMENDATION_ENGINE_BASE_URL}/pld-reports`;
    
    console.log('💾 [BFF] Salvando parecer PLD...');
    console.log('📦 [BFF] Dados do parecer:', parecerData);
    console.log('🌐 [BFF] URL:', url);
    
    const authToken = localStorage.getItem('authToken');
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'x-api-key': this.API_KEY,
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authToken}`
      },
      body: JSON.stringify(parecerData)
    });

    console.log('📡 [BFF] Response status:', response.status);

    if (!response.ok) {
      const errorText = await response.text();
      console.error('❌ [BFF] Erro ao salvar:', errorText);
      throw new Error(`Save PLD report error: ${response.status} ${response.statusText} - ${errorText}`);
    }

    const result = await response.json();
    console.log('✅ [BFF] Parecer salvo com sucesso:', result);
    return result;
  }

  /**
   * Verifica se parecer PLD já existe para os lotes selecionados
   */
  async checkPLDReportExists(batchSelections: any): Promise<any> {
    const url = `${this.RECOMMENDATION_ENGINE_BASE_URL}/pld-reports/check`;
    
    console.log('🔍 [BFF] Verificando parecer existente...');
    console.log('📦 [BFF] batchSelections:', batchSelections);
    console.log('🌐 [BFF] URL:', url);
    
    const authToken = localStorage.getItem('authToken');
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'x-api-key': this.API_KEY,
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authToken}`
      },
      body: JSON.stringify({ batchSelections })
    });

    console.log('📡 [BFF] Response status:', response.status);

    if (!response.ok) {
      const errorText = await response.text();
      console.error('❌ [BFF] Erro na resposta:', errorText);
      throw new Error(`Check PLD report error: ${response.status} ${response.statusText}`);
    }

    const result = await response.json();
    console.log('✅ [BFF] Resultado:', result);
    return result;
  }
}

// Instância singleton
export const recommendationBFFService = new RecommendationBFFService();

// Export da classe para testes
export { RecommendationBFFService };
